import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  Button,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "react-query";
import AddChannelForm from "./AddChannelForm";
import SetupStepHelp from "./setup/SetupStepHelp";
import { CHANNEL_CARDS, CHANNEL_TYPES } from "../constants/channelCatalog";
import { fetchData } from "../api/client";

const VISIBLE_CARDS = CHANNEL_CARDS.filter((c) => c.id !== CHANNEL_TYPES.DEFAULT);

export default function EtapaCanais({ onChannelsChange }) {
  const theme = useTheme();
  const { t } = useTranslation("onboarding");
  const queryClient = useQueryClient();
  const [formOpen, setFormOpen] = useState(false);
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [presetTipo, setPresetTipo] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const channelsQuery = useQuery(
    ["setup-wizard-channels"],
    () => fetchData("/channels?page=1&limit=200&search="),
    {
      staleTime: 30 * 1000,
      select: (response) => ({
        channels: Array.isArray(response?.data) ? response.data : [],
      }),
    }
  );

  const channels = channelsQuery.data?.channels || [];

  useEffect(() => {
    if (typeof onChannelsChange === "function") {
      onChannelsChange(channels);
    }
  }, [channels, onChannelsChange]);

  const updateMutation = useMutation(
    (channel) => {
      const method = channel.id ? "PUT" : "POST";
      const endpoint = channel.id ? `/channels/${channel.id}` : `/channels`;
      return fetchData(endpoint, method, channel);
    },
    {
      onSuccess: () => queryClient.invalidateQueries(["setup-wizard-channels"]),
    }
  );

  const openCreate = (card) => {
    setPresetTipo(card?.id ?? null);
    setSelectedChannel(card?.id != null ? { tipo: card.id } : {});
    setFormOpen(true);
  };

  const openEdit = (ch) => {
    setPresetTipo(null);
    setSelectedChannel(ch);
    setFormOpen(true);
  };

  const closeForm = () => {
    setFormOpen(false);
    setSelectedChannel(null);
    setPresetTipo(null);
  };

  const handleSubmitChannel = useCallback(
    async (updatedChannel) => {
      setIsProcessing(true);
      try {
        const result = await updateMutation.mutateAsync(updatedChannel);
        const createdId = result?.data?.id;
        if (!updatedChannel.id && createdId) {
          if (
            Number(updatedChannel.tipo) === CHANNEL_TYPES.EMAIL_AWS_SES &&
            updatedChannel.email_domain
          ) {
            setSelectedChannel({ ...updatedChannel, id: createdId });
            return result;
          }
          if (Number(updatedChannel.tipo) === CHANNEL_TYPES.WHATSAPP_QR) {
            setSelectedChannel({ ...updatedChannel, id: createdId });
            return result;
          }
          if (
            Number(updatedChannel.tipo) === CHANNEL_TYPES.WHATSAPP_GUPSHUP &&
            String(updatedChannel.whatsapp_calling_mode || "").trim().toLowerCase() === "gupshup_sip"
          ) {
            setSelectedChannel({ ...updatedChannel, id: createdId });
            return result;
          }
          queueMicrotask(() => {
            closeForm();
          });
          return result;
        }
        queueMicrotask(() => {
          closeForm();
        });
        return result;
      } finally {
        setIsProcessing(false);
      }
    },
    [updateMutation]
  );

  const cardByTipo = useMemo(() => {
    const m = new Map();
    VISIBLE_CARDS.forEach((c) => m.set(Number(c.id), c));
    return m;
  }, []);

  const loading = channelsQuery.isLoading || channelsQuery.isFetching;

  return (
    <Box sx={{ width: "100%" }}>
      <SetupStepHelp stepKey="channels" />

      <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ color: theme.palette.primary.main }}>
        {t("setup.channels.title")}
      </Typography>

      <Typography variant="body1" gutterBottom>
        {t("setup.channels.intro")}
      </Typography>

      <Typography variant="body2" color="text.secondary" mb={2}>
        {t("setup.channels.hintSkip")}
      </Typography>

      {channelsQuery.error ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {t("setup.channels.loadError")}
        </Alert>
      ) : null}

      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
        {t("setup.channels.catalogHeading")}
      </Typography>

      <Grid container spacing={2}>
        {VISIBLE_CARDS.map((card) => {
          const Icon = card.icon;
          return (
            <Grid item xs={12} sm={6} md={4} key={card.key}>
              <Card
                variant="outlined"
                sx={{
                  p: 2,
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                  borderRadius: 2,
                }}
              >
                <Box display="flex" alignItems="center" gap={1}>
                  {Icon ? <Icon sx={{ color: card.color || theme.palette.primary.main }} /> : null}
                  <Typography variant="subtitle1" fontWeight={700}>
                    {card.label}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
                  {card.description}
                </Typography>
                <Button variant="contained" size="small" onClick={() => openCreate(card)}>
                  {t("setup.channels.configure")}
                </Button>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      <Typography variant="subtitle1" fontWeight={600} sx={{ mt: 4, mb: 1 }}>
        {t("setup.channels.existingHeading")}
      </Typography>

      {loading ? (
        <Box display="flex" alignItems="center" gap={1} py={2}>
          <CircularProgress size={22} />
          <Typography variant="body2" color="text.secondary">
            {t("setup.channels.loading")}
          </Typography>
        </Box>
      ) : channels.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {t("setup.channels.noneYet")}
        </Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t("setup.channels.colName")}</TableCell>
              <TableCell>{t("setup.channels.colType")}</TableCell>
              <TableCell align="right">{t("setup.channels.colActions")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {channels.map((ch) => {
              const tipoNum = Number(ch.tipo);
              const card = cardByTipo.get(tipoNum);
              const typeLabel = card?.label || `tipo ${ch.tipo}`;
              return (
                <TableRow key={ch.id}>
                  <TableCell>{ch.nome || "—"}</TableCell>
                  <TableCell>
                    <Chip size="small" label={typeLabel} variant="outlined" />
                  </TableCell>
                  <TableCell align="right">
                    <Button size="small" onClick={() => openEdit(ch)}>
                      {t("setup.channels.edit")}
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}

      <Dialog
        open={formOpen}
        onClose={closeForm}
        fullWidth
        maxWidth="md"
        aria-labelledby="setup-channel-form-title"
      >
        <DialogTitle id="setup-channel-form-title">
          {selectedChannel?.id ? t("setup.channels.dialogEdit") : t("setup.channels.dialogNew")}
        </DialogTitle>
        <DialogContent dividers sx={{ pt: 1 }}>
          {formOpen && selectedChannel ? (
            <AddChannelForm
              key={
                selectedChannel?.id != null
                  ? String(selectedChannel.id)
                  : `new-${presetTipo ?? "x"}`
              }
              channel={selectedChannel}
              isLoading={loading || isProcessing}
              onClose={closeForm}
              onSubmit={handleSubmitChannel}
            />
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeForm}>{t("setup.channels.dialogClose")}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
